import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  CompanyService,
  RecentSearch,
  RecentSearchesService,
  ShortcutService,
} from '@app/core';
import { CompanyShortInfo, ListType, formatQuery } from '@app/model';
import { Subject, Subscription, combineLatest, of, timer } from 'rxjs';
import { catchError, debounce, map, switchMap, tap } from 'rxjs/operators';

export type SearchOption =
  | { kind: 'company'; company: CompanyShortInfo }
  | { kind: 'list'; list: ListType }
  | { kind: 'recent'; search: RecentSearch }
  | { kind: 'all'; text: string };

export interface SearchGroup {
  key: 'recent' | 'companies' | 'lists' | 'all';
  label: string | null;
  options: SearchOption[];
}

/** Companies are searched from this many characters on, like before. */
export const COMPANY_MIN_LENGTH = 3;
const MAX_COMPANIES = 8;
const MAX_LISTS = 5;

/**
 * The groups of the suggestions: without text the recent searches; with text
 * the companies of the API, the lists whose name contains the text and the
 * way to all results in the search page.
 */
export const searchGroups = (
  text: string,
  companies: CompanyShortInfo[],
  lists: ListType[],
  recent: RecentSearch[]
): SearchGroup[] => {
  const needle = text.trim();
  if (!needle) {
    return recent.length
      ? [
          {
            key: 'recent',
            label: 'globalSearch.group.recent',
            options: recent.map((search) => ({ kind: 'recent', search })),
          },
        ]
      : [];
  }
  const lower = needle.toLowerCase();
  const matchingLists = lists
    .filter((list) => list.name.toLowerCase().includes(lower))
    .sort(
      (a, b) =>
        Number(!a.name.toLowerCase().startsWith(lower)) -
        Number(!b.name.toLowerCase().startsWith(lower))
    )
    .slice(0, MAX_LISTS);
  const groups: SearchGroup[] = [];
  if (companies.length) {
    groups.push({
      key: 'companies',
      label: 'globalSearch.group.companies',
      options: companies
        .slice(0, MAX_COMPANIES)
        .map((company) => ({ kind: 'company', company })),
    });
  }
  if (matchingLists.length) {
    groups.push({
      key: 'lists',
      label: 'globalSearch.group.lists',
      options: matchingLists.map((list) => ({ kind: 'list', list })),
    });
  }
  groups.push({
    key: 'all',
    label: null,
    options: [{ kind: 'all', text: needle }],
  });
  return groups;
};

/** The URL of an option. */
export const optionUrl = (option: SearchOption): unknown[] => {
  switch (option.kind) {
    case 'company':
      return ['/companies', option.company.business_id, 'overview'];
    case 'list':
      return ['/lists', option.list.id];
    case 'recent':
      return ['/search', option.search.state];
    case 'all':
      return ['/search'];
  }
};

/** The query of an option: the text as the name for all results. */
export const optionQueryParams = (
  option: SearchOption
): Record<string, string> =>
  option.kind === 'all'
    ? {
        q: formatQuery([
          { field: 'name', value: { kind: 'text', value: option.text } },
        ]),
      }
    : {};

let nextId = 0;

/**
 * The search of the top bar: companies, lists and recent searches as grouped
 * suggestions, and the way to all results in the search page. `/` and
 * Ctrl/Cmd+K focus it, the arrow keys move through the suggestions.
 */
@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GlobalSearchComponent implements OnDestroy {
  private companyService = inject(CompanyService);
  private recentSearches = inject(RecentSearchesService);
  private router = inject(Router);
  private changeDetector = inject(ChangeDetectorRef);

  @ViewChild('input') input?: ElementRef<HTMLInputElement>;

  public readonly id = `global-search-${nextId++}`;
  public text = '';
  public open = false;
  public loading = false;
  public groups: SearchGroup[] = [];
  public options: SearchOption[] = [];
  public activeIndex = -1;

  private texts = new Subject<string>();
  private subscription: Subscription;
  private shortcuts = inject(ShortcutService);
  private unregister = [
    this.shortcuts.register({ keys: '/', handler: () => this.focusShown() }),
    this.shortcuts.register({
      keys: 'mod+k',
      inFields: true,
      handler: () => this.focusShown(),
    }),
  ];

  constructor() {
    const companies$ = this.texts.pipe(
      debounce((text) =>
        text.trim().length >= COMPANY_MIN_LENGTH ? timer(300) : of(0)
      ),
      switchMap((text) => {
        const needle = text.trim();
        if (needle.length < COMPANY_MIN_LENGTH) {
          return of({ text, companies: [] as CompanyShortInfo[] });
        }
        this.loading = true;
        this.changeDetector.markForCheck();
        return this.companyService.searchCompanies(needle).pipe(
          catchError(() => of([] as CompanyShortInfo[])),
          map((companies) => ({ text, companies }))
        );
      }),
      tap(() => (this.loading = false))
    );
    this.subscription = combineLatest([
      companies$,
      this.companyService.allLists$,
      this.recentSearches.recent$,
    ]).subscribe(([{ text, companies }, lists, recent]) => {
      // A result for an older text waits for the one of the current text.
      if (text !== this.text) {
        return;
      }
      this.setGroups(searchGroups(text, companies, lists, recent));
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.unregister.forEach((x) => x());
  }

  /** `/` and Ctrl/Cmd+K focus the shown search; a page bar takes `/` first. */
  focusShown = () => {
    const input = this.input?.nativeElement;
    if (!input || !this.isShown(input)) {
      return false;
    }
    input.focus();
    return true;
  };

  /** The header hides one of its two searches by the screen width. */
  isShown(element: HTMLElement) {
    return element.offsetParent !== null;
  }

  onFocus() {
    this.open = true;
    this.texts.next(this.text);
  }

  onInput(text: string) {
    this.text = text;
    this.open = true;
    this.activeIndex = -1;
    this.texts.next(text);
  }

  onKeydown(event: KeyboardEvent) {
    const count = this.options.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.open = true;
        this.activeIndex = count ? (this.activeIndex + 1) % count : -1;
        this.scrollActive();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.open = true;
        this.activeIndex = count ? (this.activeIndex - 1 + count) % count : -1;
        this.scrollActive();
        break;
      case 'Enter': {
        const option =
          this.options[this.activeIndex] ??
          (this.text.trim()
            ? ({ kind: 'all', text: this.text.trim() } as SearchOption)
            : null);
        if (option) {
          event.preventDefault();
          this.select(option);
        }
        break;
      }
      case 'Escape':
        if (this.open) {
          event.preventDefault();
          this.close();
        } else {
          this.input?.nativeElement.blur();
        }
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  /** Focus left the search, e.g. by a click elsewhere. */
  onFocusOut(event: FocusEvent) {
    const host = (event.currentTarget as HTMLElement | null) ?? null;
    if (!host || !host.contains(event.relatedTarget as Node | null)) {
      this.close();
    }
  }

  select(option: SearchOption) {
    this.router.navigate(optionUrl(option), {
      queryParams: optionQueryParams(option),
    });
    this.text = '';
    this.close();
    this.input?.nativeElement.blur();
  }

  indexOf(option: SearchOption) {
    return this.options.indexOf(option);
  }

  /** City, domain and subsectors of a company suggestion. */
  meta(company: CompanyShortInfo) {
    const sectors = (company.companies_subsectors ?? [])
      .filter((x) => x.subsector_id !== null)
      .map((x) => x.subsectors.name)
      .join(', ');
    return [company.city, company.domain, sectors]
      .filter((x) => !!x)
      .join(' · ');
  }

  private close() {
    this.open = false;
    this.activeIndex = -1;
  }

  private setGroups(groups: SearchGroup[]) {
    this.groups = groups;
    this.options = groups.flatMap((group) => group.options);
    if (this.activeIndex >= this.options.length) {
      this.activeIndex = -1;
    }
    this.changeDetector.markForCheck();
  }

  private scrollActive() {
    setTimeout(() =>
      document
        .getElementById(`${this.id}-option-${this.activeIndex}`)
        ?.scrollIntoView({ block: 'nearest' })
    );
  }
}
